import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Localizable } from './localize-field';

/**
 * Backs two-way inputs on admin edit screens so edits route to the right
 * slot based on the active language:
 *
 *  - Active lang == default lang  -> base field (e.g. node.title)
 *  - Active lang != default lang  -> node._i18n[lang][field], created lazily
 *                                    and pruned when cleared, so absent
 *                                    translations fall back to the base.
 */
@Injectable({ providedIn: 'root' })
export class LocalizedEditService {

  constructor(private readonly _translocoService: TranslocoService) {}

  get defaultLang(): string {
    return this._translocoService.getDefaultLang();
  }

  get activeLang(): string {
    return this._translocoService.getActiveLang();
  }

  get isEditingBase(): boolean {
    return this.activeLang === this.defaultLang;
  }

  /** Value to bind into an input for the active lang. */
  get<T extends Localizable>(node: T | undefined | null, field: keyof T & string): string {
    if (!node) {
      return '';
    }
    if (this.isEditingBase) {
      const v = (node as any)[field];
      return v == null ? '' : String(v);
    }
    const v = node._i18n?.[this.activeLang]?.[field];
    return v == null ? '' : String(v);
  }

  /** Write an edit into the correct slot based on active lang. */
  set<T extends Localizable>(node: T | undefined | null, field: keyof T & string, value: string): void {
    if (!node) {
      return;
    }
    if (this.isEditingBase) {
      (node as any)[field] = value ?? '';
      return;
    }
    const lang = this.activeLang;
    const isEmpty = value === '' || value === null || value === undefined;
    if (isEmpty) {
      const langBlock = node._i18n?.[lang];
      if (langBlock) {
        delete langBlock[field];
        if (Object.keys(langBlock).length === 0) {
          delete node._i18n![lang];
        }
        // Do NOT delete the top-level `_i18n` object even when it becomes empty: a runtime
        // step and its StepConfig share this SAME object by reference (see
        // GenericStepFactory.create). Replacing or removing it would break that identity and
        // re-introduce the "translation edits lost on save" bug. Pruning the per-lang and
        // per-field keys is sufficient; an empty `{}` resolves identically to absent.
      }
      return;
    }
    if (!node._i18n) {
      node._i18n = {};
    }
    if (!node._i18n[lang]) {
      node._i18n[lang] = {};
    }
    node._i18n[lang][field] = value;
  }

  /** Placeholder to show when editing a non-default lang and the slot is empty. */
  basePlaceholder<T extends Localizable>(node: T | undefined | null, field: keyof T & string): string {
    if (!node || this.isEditingBase) {
      return '';
    }
    const v = (node as any)[field];
    return v == null ? '' : String(v);
  }
}
